import {
  ShoppingCart, Store, ShoppingBag, PackageOpen, CreditCard,
  Briefcase, Building2, Rocket, Layers, FolderKanban, User,
  Settings, Calendar, Hospital, Stethoscope, UserRound,
  UtensilsCrossed, Hotel, Coffee,
  GraduationCap, School, Building, BookOpen, Video, Users,
  FileText, Newspaper, MessageCircle
} from 'lucide-react';

export const websiteCategories = [
  {
    id: 'ecommerce-stores',
    title: 'E-commerce & Online Stores',
    description: 'Sell products online with complete store management',
    priority: 1,
    websites: [
      {
        id: 1,
        name: 'E-commerce Website',
        icon: ShoppingCart,
        tagline: 'Sell anything online',
        whatFor: 'Complete online store with product listings, shopping cart, secure payments, order management, and customer accounts',
        whoUses: 'Retailers, brands, and businesses wanting to sell products online with inventory tracking and automated order processing',
        color: 'green'
      },
      {
        id: 2,
        name: 'Online Store',
        icon: Store,
        tagline: 'Your 24/7 shop',
        whatFor: 'Simple storefront to showcase products, accept orders, and receive payments with easy checkout',
        whoUses: 'Small businesses, local shops, and individual sellers starting their online presence',
        color: 'emerald'
      },
      {
        id: 3,
        name: 'Marketplace Website',
        icon: ShoppingBag,
        tagline: 'Connect buyers & sellers',
        whatFor: 'Platform where multiple vendors can sell products, manage their inventory, and earn revenue through commissions',
        whoUses: 'Entrepreneurs building platforms like Amazon, Etsy, or local marketplaces for their region or niche',
        color: 'orange'
      },
      {
        id: 4,
        name: 'Multi-Vendor Website',
        icon: PackageOpen,
        tagline: 'Empower multiple sellers',
        whatFor: 'Allow multiple sellers with separate dashboards to manage products, orders, and payments independently',
        whoUses: 'Business owners creating vendor platforms, wholesale marketplaces, or reseller networks',
        color: 'amber'
      },
      {
        id: 5,
        name: 'Subscription Store',
        icon: CreditCard,
        tagline: 'Recurring revenue model',
        whatFor: 'Sell subscriptions, memberships, or recurring services with automated billing and renewal management',
        whoUses: 'Subscription box businesses, membership clubs, SaaS products, and recurring service providers',
        color: 'teal'
      }
    ]
  },
  {
    id: 'business-professional',
    title: 'Business & Professional Websites',
    description: 'Professional websites to grow your business',
    priority: 2,
    websites: [
      {
        id: 6,
        name: 'Business Website',
        icon: Briefcase,
        tagline: 'Your digital presence',
        whatFor: 'Showcase your services, build credibility, share contact information, and generate leads for your business',
        whoUses: 'Small businesses, consultants, professionals, and service providers wanting to establish online presence',
        color: 'blue'
      },
      {
        id: 7,
        name: 'Company Website',
        icon: Building2,
        tagline: 'Corporate excellence',
        whatFor: 'Professional company profile with team showcase, achievements, client testimonials, and corporate information',
        whoUses: 'Established companies, corporations, and enterprises needing a professional corporate presence',
        color: 'blue'
      },
      {
        id: 8,
        name: 'Startup Website',
        icon: Rocket,
        tagline: 'Launch your vision',
        whatFor: 'Present your innovative idea, attract investors, recruit talent, and build early customer traction',
        whoUses: 'Startups, entrepreneurs, and innovators launching new products or disruptive services',
        color: 'purple'
      },
      {
        id: 9,
        name: 'Agency Website',
        icon: Layers,
        tagline: 'Showcase expertise',
        whatFor: 'Display portfolio, highlight services, share case studies, and attract high-value clients',
        whoUses: 'Digital agencies, marketing firms, creative studios, and consulting agencies',
        color: 'indigo'
      },
      {
        id: 10,
        name: 'Portfolio Website',
        icon: FolderKanban,
        tagline: 'Display your work',
        whatFor: 'Showcase your best projects, skills, achievements, and attract clients or job opportunities',
        whoUses: 'Designers, developers, artists, freelancers, and creative professionals',
        color: 'purple'
      },
      {
        id: 11,
        name: 'Personal Website',
        icon: User,
        tagline: 'Your digital identity',
        whatFor: 'Personal branding, resume showcase, blog, and professional online presence',
        whoUses: 'Professionals, job seekers, freelancers, and individuals building personal brand',
        color: 'slate'
      }
    ]
  },
  {
    id: 'service-booking-industry',
    title: 'Service, Booking & Industry Websites',
    description: 'Streamline bookings and service delivery',
    priority: 3,
    websites: [
      {
        id: 12,
        name: 'Service Website',
        icon: Settings,
        tagline: 'Promote your services',
        whatFor: 'Showcase services, pricing, testimonials, and generate leads with inquiry forms',
        whoUses: 'Service-based businesses, consultants, maintenance services, and professional services',
        color: 'gray'
      },
      {
        id: 13,
        name: 'Booking Website',
        icon: Calendar,
        tagline: 'Reserve with ease',
        whatFor: 'Online booking system with calendar, availability management, automated confirmations, and reminders',
        whoUses: 'Any business requiring appointments - salons, spas, venues, consultants, and service providers',
        color: 'sky'
      },
      {
        id: 14,
        name: 'Appointment Website',
        icon: Calendar,
        tagline: 'Schedule made simple',
        whatFor: 'Appointment scheduling with time slots, automated reminders, rescheduling, and cancellation management',
        whoUses: 'Healthcare providers, consultants, coaches, and businesses managing client appointments',
        color: 'blue'
      },
      {
        id: 15,
        name: 'Hospital Website',
        icon: Hospital,
        tagline: 'Healthcare online',
        whatFor: 'Hospital information, department details, doctor schedules, appointment booking, and patient portal',
        whoUses: 'Hospitals, medical centers, and large healthcare facilities',
        color: 'red'
      },
      {
        id: 16,
        name: 'Clinic Website',
        icon: Stethoscope,
        tagline: 'Care made accessible',
        whatFor: 'Clinic services, doctor information, appointment booking, and patient communication',
        whoUses: 'Private clinics, diagnostic centers, specialty clinics, and medical practices',
        color: 'rose'
      },
      {
        id: 17,
        name: 'Doctor Website',
        icon: UserRound,
        tagline: 'Your online practice',
        whatFor: 'Professional profile, specialization showcase, online consultation booking, and patient reviews',
        whoUses: 'Individual doctors, specialists, surgeons, and healthcare practitioners',
        color: 'pink'
      },
      {
        id: 18,
        name: 'Restaurant Website',
        icon: UtensilsCrossed,
        tagline: 'Taste begins online',
        whatFor: 'Menu display, online ordering, table reservations, location details, and customer reviews',
        whoUses: 'Restaurants, eateries, food chains, and food service businesses',
        color: 'orange'
      },
      {
        id: 19,
        name: 'Hotel Website',
        icon: Hotel,
        tagline: 'Welcome guests',
        whatFor: 'Room showcase, online booking, amenities information, photo gallery, and reservation management',
        whoUses: 'Hotels, resorts, lodges, guest houses, and hospitality businesses',
        color: 'amber'
      },
      {
        id: 20,
        name: 'Cafe Website',
        icon: Coffee,
        tagline: 'Brew your presence',
        whatFor: 'Menu showcase, atmosphere photos, special events, online ordering, and location information',
        whoUses: 'Cafes, coffee shops, tea houses, and beverage businesses',
        color: 'yellow'
      }
    ]
  },
  {
    id: 'education-community',
    title: 'Education, Community & Platforms',
    description: 'Learning, content, and community platforms',
    priority: 4,
    websites: [
      {
        id: 21,
        name: 'Educational Website',
        icon: GraduationCap,
        tagline: 'Share knowledge',
        whatFor: 'Educational content, learning resources, tutorials, and course information',
        whoUses: 'Educators, training institutes, and educational content creators',
        color: 'blue'
      },
      {
        id: 22,
        name: 'School Website',
        icon: School,
        tagline: 'Connect school & parents',
        whatFor: 'School information, admissions, events, announcements, student portal, and parent communication',
        whoUses: 'K-12 schools, primary schools, and educational institutions',
        color: 'cyan'
      },
      {
        id: 23,
        name: 'College Website',
        icon: Building,
        tagline: 'Higher education hub',
        whatFor: 'Course catalog, admissions, faculty profiles, campus life, research, and student services',
        whoUses: 'Colleges, universities, and higher education institutions',
        color: 'indigo'
      },
      {
        id: 24,
        name: 'Coaching Website',
        icon: BookOpen,
        tagline: 'Guide to success',
        whatFor: 'Class schedules, course details, enrollment, student performance tracking, and study materials',
        whoUses: 'Coaching centers, tutoring services, test prep institutes, and training academies',
        color: 'violet'
      },
      {
        id: 25,
        name: 'Course Website',
        icon: Video,
        tagline: 'Teach online',
        whatFor: 'Sell and deliver online courses with video lessons, quizzes, assignments, and certificates',
        whoUses: 'Course creators, trainers, subject experts, and online educators',
        color: 'purple'
      },
      {
        id: 26,
        name: 'LMS Website',
        icon: Users,
        tagline: 'Complete learning system',
        whatFor: 'Full learning management with courses, assessments, progress tracking, reports, and user management',
        whoUses: 'Educational institutions, corporate training departments, and e-learning platforms',
        color: 'fuchsia'
      },
      {
        id: 27,
        name: 'Blog Website',
        icon: FileText,
        tagline: 'Share your voice',
        whatFor: 'Article publishing, content management, categories, comments, and audience engagement',
        whoUses: 'Bloggers, writers, content creators, journalists, and thought leaders',
        color: 'orange'
      },
      {
        id: 28,
        name: 'News Website',
        icon: Newspaper,
        tagline: 'Breaking news platform',
        whatFor: 'News articles, breaking news alerts, categories, multimedia content, and reader engagement',
        whoUses: 'News agencies, journalists, media houses, and online publications',
        color: 'red'
      },
      {
        id: 29,
        name: 'Community Website',
        icon: Users,
        tagline: 'Build your tribe',
        whatFor: 'Member profiles, discussions, events, content sharing, and community engagement features',
        whoUses: 'Online communities, interest groups, clubs, and social networks',
        color: 'pink'
      },
      {
        id: 30,
        name: 'Forum Website',
        icon: MessageCircle,
        tagline: 'Discussions & answers',
        whatFor: 'Topic-based discussions, Q&A format, user reputation, moderation tools, and knowledge sharing',
        whoUses: 'Support communities, discussion platforms, Q&A sites, and help forums',
        color: 'indigo'
      }
    ]
  }
];
